import React, { useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "react-toastify";

const toText = (v) => {
  if (v === null || v === undefined) return "";
  return String(v);
};

// jsPDF default fonts are limited; keep text printable & compact.
const sanitizePdfText = (value) => {
  const s = toText(value);
  return (
    s
      // remove control chars
      .replace(/[\u0000-\u001F\u007F]/g, " ")
      // replace non-breaking space
      .replace(/\u00A0/g, " ")
      // collapse whitespace
      .replace(/\s+/g, " ")
      .trim()
      // keep a safe subset (prevents "garbled" glyphs in some PDFs)
      .replace(/[^\x20-\x7E]/g, "")
      .trim()
  );
};

// Keeps column order aligned with ConsolidarePpa.jsx
const SUBHEAD_COLUMNS = [
  { key: "name", label: "Caption" },
  { key: "provisional_budget", label: "Provisioned Budget" },
  { key: "openingBalance", label: "Opening Balance" },
  { key: "budget", label: "Release Budget" },
  { key: "reappropriate_received", label: "Reappropriation Budget" },
  { key: "surrender_received", label: "Surrender Budget" },
  { key: "totalBudgetReceived", label: "Total Budget" },
  { key: "allocation", label: "Limit Allocated to child" },
  { key: "reappropriate_given", label: "Reappropriation transferred" },
  { key: "surrender_given", label: "Surrender to parent" },
  { key: "totalBudgetTransferred", label: "Total Budget Transferred" },
  { key: "budgetAllowed", label: "Budget / Limit Allowed" },
  { key: "vendor", label: "Vendor" },
  { key: "dbt", label: "DBT" },
  { key: "salary", label: "Salary" },
  { key: "totalSuccessfulPayment", label: "Total Successfully Payment" },
  { key: "pendingPPA", label: "PPA (Under Approval)" },
  { key: "totalPpaPayment", label: "Total Budget" },
  { key: "balanceOnDate", label: "Balance on (Date)" },
  { key: "commulative", label: "Commulative" },
  { key: "commulative_expenditure", label: "Commulative Expenditure" },
];

const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const buildRows = (departments) => {
  const rows = [];
  (departments ?? []).forEach((dept) => {
    const deptName = sanitizePdfText(dept?.name ?? dept?.department_name ?? "Department");
    rows.push([
      { content: `Department: ${deptName}`, colSpan: SUBHEAD_COLUMNS.length },
    ]);

    (dept?.schemes ?? []).forEach((scheme) => {
      const schemeName = sanitizePdfText(
        scheme?.name ?? scheme?.scheme_name ?? scheme?.code ?? "Scheme"
      );
      rows.push([
        { content: `Scheme: ${schemeName}`, colSpan: SUBHEAD_COLUMNS.length },
      ]);

      (scheme?.sub_head_list ?? []).forEach((subhead) => {
        const openingBalance = toNum(subhead?.openingBalance);
        const budget = toNum(subhead?.budget);
        const reappropriateReceived = toNum(subhead?.reappropriate_received);
        const surrenderReceived = toNum(subhead?.surrender_received);
        const totalBudgetReceived =
          openingBalance + budget + reappropriateReceived + surrenderReceived;

        const allocation = toNum(subhead?.allocation);
        const reappropriateGiven = toNum(subhead?.reappropriate_given);
        const surrenderGiven = toNum(subhead?.surrender_given);
        const totalBudgetTransferred = allocation + reappropriateGiven + surrenderGiven;

        const budgetAllowed = totalBudgetReceived - totalBudgetTransferred;

        const vendor = toNum(subhead?.vendor);
        const dbt = toNum(subhead?.dbt);
        const salary = toNum(subhead?.salary);
        const totalSuccessfulPayment = vendor + dbt + salary;

        const pendingPPA = toNum(subhead?.pendingPPA);
        const totalPpaPayment = totalSuccessfulPayment + pendingPPA;

        const balanceOnDate = budgetAllowed - totalPpaPayment;

        const values = {
          ...subhead,
          openingBalance,
          budget,
          reappropriate_received: reappropriateReceived,
          surrender_received: surrenderReceived,
          totalBudgetReceived,
          allocation,
          reappropriate_given: reappropriateGiven,
          surrender_given: surrenderGiven,
          totalBudgetTransferred,
          budgetAllowed,
          vendor,
          dbt,
          salary,
          totalSuccessfulPayment,
          pendingPPA,
          totalPpaPayment,
          balanceOnDate,
        };

        rows.push(SUBHEAD_COLUMNS.map((c) => sanitizePdfText(values?.[c.key] ?? "")));
      });
    });
  });

  return rows;
};

const hasExportableSubheadRows = (departments) => {
  return (departments ?? []).some((dept) =>
    (dept?.schemes ?? []).some(
      (scheme) => Array.isArray(scheme?.sub_head_list) && scheme.sub_head_list.length > 0
    )
  );
};

export default function ExportConsolidatePpaPdfButton({
  departments,
  fromDate,
  toDate,
  fileName,
  title = "Consolidate PPA Report",
  className = "navigation_button",
  disabled,
  exportEngine = "print", // "print" keeps Hindi/Unicode; "autotable" is pure-PDF table
  children = "Export to PDF",
}) {
  const safeFileName = useMemo(() => {
    if (fileName) return fileName;
    const f = fromDate ? String(fromDate) : "from";
    const t = toDate ? String(toDate) : "to";
    return `consolidate-ppa_${f}_${t}.pdf`;
  }, [fileName, fromDate, toDate]);

  const hasData = useMemo(() => hasExportableSubheadRows(departments), [departments]);
  const isInactive = disabled || !hasData;

  const buildHtmlTable = () => {
    const headerCells = SUBHEAD_COLUMNS.map((c) => `<th>${sanitizePdfText(c.label) || ""}</th>`).join("");

    const rows = [];
    (departments ?? []).forEach((dept) => {
      const deptName = toText(dept?.name ?? dept?.department_name ?? "Department");
      rows.push(
        `<tr class="sep"><td colspan="${SUBHEAD_COLUMNS.length}"><strong>Department:</strong> ${deptName}</td></tr>`
      );

      (dept?.schemes ?? []).forEach((scheme) => {
        const schemeName = toText(
          scheme?.name ?? scheme?.scheme_name ?? scheme?.code ?? "Scheme"
        );
        rows.push(
          `<tr class="sep"><td colspan="${SUBHEAD_COLUMNS.length}"><strong>Scheme:</strong> ${schemeName}</td></tr>`
        );

        (scheme?.sub_head_list ?? []).forEach((subhead) => {
          // reuse same calculations so numbers match UI
          const openingBalance = toNum(subhead?.openingBalance);
          const budget = toNum(subhead?.budget);
          const reappropriateReceived = toNum(subhead?.reappropriate_received);
          const surrenderReceived = toNum(subhead?.surrender_received);
          const totalBudgetReceived =
            openingBalance + budget + reappropriateReceived + surrenderReceived;

          const allocation = toNum(subhead?.allocation);
          const reappropriateGiven = toNum(subhead?.reappropriate_given);
          const surrenderGiven = toNum(subhead?.surrender_given);
          const totalBudgetTransferred = allocation + reappropriateGiven + surrenderGiven;

          const budgetAllowed = totalBudgetReceived - totalBudgetTransferred;

          const vendor = toNum(subhead?.vendor);
          const dbt = toNum(subhead?.dbt);
          const salary = toNum(subhead?.salary);
          const totalSuccessfulPayment = vendor + dbt + salary;

          const pendingPPA = toNum(subhead?.pendingPPA);
          const totalPpaPayment = totalSuccessfulPayment + pendingPPA;

          const balanceOnDate = budgetAllowed - totalPpaPayment;

          const values = {
            ...subhead,
            openingBalance,
            budget,
            reappropriate_received: reappropriateReceived,
            surrender_received: surrenderReceived,
            totalBudgetReceived,
            allocation,
            reappropriate_given: reappropriateGiven,
            surrender_given: surrenderGiven,
            totalBudgetTransferred,
            budgetAllowed,
            vendor,
            dbt,
            salary,
            totalSuccessfulPayment,
            pendingPPA,
            totalPpaPayment,
            balanceOnDate,
          };

          const tds = SUBHEAD_COLUMNS.map((c) => `<td>${toText(values?.[c.key] ?? "")}</td>`).join("");
          rows.push(`<tr>${tds}</tr>`);
        });
      });
    });

    const headerText = `${title}${fromDate || toDate ? ` (${fromDate ?? ""} - ${toDate ?? ""})` : ""}`;

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${sanitizePdfText(title)}</title>
    <style>
      body { font-family: Arial, Helvetica, sans-serif; padding: 16px; }
      h2 { margin: 0 0 12px 0; font-size: 16px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #cfd4dc; padding: 6px; font-size: 10px; vertical-align: top; }
      thead th { background: #f0f0f0; }
      tr.sep td { background: #e1ebff; font-weight: 700; }
      @media print { body { padding: 0; } }
    </style>
  </head>
  <body>
    <h2>${headerText}</h2>
    <table>
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${rows.join("")}</tbody>
    </table>
  </body>
</html>`;
  };

  const onExport = () => {
    if (isInactive) {
      toast.info("No data to export", { position: toast.POSITION.TOP_CENTER });
      return;
    }
    if (exportEngine === "print") {
      const w = window.open("", "_blank");
      if (!w) return;
      w.document.open();
      w.document.write(buildHtmlTable());
      w.document.close();
      w.focus();
      // give browser a tick to layout fonts before print
      setTimeout(() => {
        w.print();
      }, 200);
      return;
    }

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

    const headerText = `${title}${fromDate || toDate ? ` (${fromDate ?? ""} - ${toDate ?? ""})` : ""}`;
    doc.setFontSize(12);
    doc.text(headerText, 40, 30);

    const head = [SUBHEAD_COLUMNS.map((c) => c.label)];
    const body = buildRows(departments);

    autoTable(doc, {
      head,
      body,
      startY: 45,
      theme: "grid",
      styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
      headStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: "bold" },
      didParseCell: (data) => {
        // Style department/scheme separator rows (colSpan rows)
        if (Array.isArray(data.row.raw) && data.row.raw[0]?.colSpan) {
          data.cell.styles.fillColor = [225, 235, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.halign = "left";
        }
      },
      margin: { left: 40, right: 40, top: 30, bottom: 30 },
    });

    doc.save(safeFileName);
  };

  return (
    <button
      type="button"
      className={className}
      aria-disabled={isInactive}
      disabled={!!disabled}
      onClick={onExport}
      title={isInactive ? "No data to export" : "Download PDF"}
      style={
        isInactive
          ? { opacity: 0.55, cursor: "not-allowed" }
          : undefined
      }
    >
      {children}
    </button>
  );
}

